export type MenuItem = {
  id: number;
  category_id: number;
  label: string;
  href: string;
  sort_order: number;
};

export type NavCategory = {
  id: number;
  slug: string;
  name: string;
  tagline: string | null;
  sort_order: number;
  items: MenuItem[];
};

export type PageSummary = {
  slug: string;
  title: string;
  summary: string | null;
  category_id: number | null;
};

export type PageDetail = PageSummary & {
  body: string | null;
  category_slug: string | null;
  category_name: string | null;
};

export type StockChoice = {
  symbol: string;
  label: string;
  timeframe: string;
};

export type AiModelSummary = {
  id: string;
  name: string;
  menu_label: string;
  client: string;
  method: string;
  path: string;
  description: string;
  stock_choices?: StockChoice[];
  default_stock?: string;
};

export type AiModelInvokeResult = {
  model_id: string;
  model_name: string;
  upstream_url?: string;
  upstream_status: number;
  upstream_content_type: string;
  upstream_json: unknown;
  upstream_body: string | null;
};

/** Keep in sync with CustomerContext TOKEN_KEY. */
export const CUSTOMER_TOKEN_KEY = "tnk_customer_token";

/** Remove any previously stored API key from the browser (never keep secrets in session). */
export function clearSecurityApiKey(): void {
  try {
    sessionStorage.removeItem("tnk_security_api_key");
  } catch {
    /* ignore */
  }
}

/** Bearer for locked /api/* when gate is on. Do not put SECURITY_API_KEY in the browser. */
export function customerAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    clearSecurityApiKey();
    const t = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (t) headers.Authorization = `Bearer ${t}`;
  } catch {
    /* ignore */
  }
  return headers;
}

/** fetch() that attaches customer Bearer + security key when present. */
export async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  for (const [k, v] of Object.entries(customerAuthHeaders())) {
    if (!headers.has(k)) headers.set(k, v);
  }
  return fetch(path, { ...init, headers });
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) {
    throw new Error(`API ${path} failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export function fetchNav(): Promise<NavCategory[]> {
  return apiGet<NavCategory[]>("/api/nav");
}

export function fetchPage(slug: string): Promise<PageDetail> {
  return apiGet<PageDetail>(`/api/pages/${encodeURIComponent(slug)}`);
}

export function fetchAiModels(): Promise<AiModelSummary[]> {
  return apiGet<AiModelSummary[]>("/api/ai-models");
}

export function fetchAiModel(modelId: string): Promise<AiModelSummary> {
  return apiGet<AiModelSummary>(`/api/ai-models/${encodeURIComponent(modelId)}`);
}

export async function invokeAiModel(
  modelId: string,
  body?: Record<string, unknown>,
): Promise<AiModelInvokeResult> {
  const res = await apiFetch(`/api/ai-models/${encodeURIComponent(modelId)}/invoke`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(`API invoke failed (${res.status})`);
  }
  return res.json() as Promise<AiModelInvokeResult>;
}
